import { useState } from "react";
import ctMachineImg from "../assests/news-mri-scanner.webp";
import mriMachineImg from "../assests/ct_scan.jpg";
import indurBoard from "../assests/indur_board.jpeg";
import indurBoard2 from "../assests/indur_board2.jpeg";
import indurEntrance from "../assests/indur_enterance.jpeg";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  CheckCircle,
  AlertCircle,
  Zap,
  Eye,
  Activity,
  ChevronRight,
} from "lucide-react";
import {
  staggerContainer,
  fadeUp,
  fadeLeft,
  fadeRight,
  scaleIn,
} from "../animations/variants";

/* ─── Machine images ─────────────────────────────────────────────────────
   CT gantry ring and MRI closed-bore superconducting magnet photos      */

/* CT Scanner — local machine photo */
const CT_MACHINE_IMG = mriMachineImg;

/* MRI Scanner — local machine photo */
const MRI_MACHINE_IMG = ctMachineImg;

/* Extra machine-angle shots used inside the tab detail section */
const CT_ANGLES = [mriMachineImg, ctMachineImg, mriMachineImg];

const MRI_ANGLES = [ctMachineImg, mriMachineImg, ctMachineImg];

/* ─── DATA ─────────────────────────────────────────────────────────────── */
const scans = {
  ct: {
    id: "ct",
    label: "CT Scan",
    sublabel: "Computed Tomography",
    color: "#0ea5e9",
    lightBg: "bg-sky-50",
    textColor: "text-sky-600",
    borderColor: "border-sky-200",
    machineImg: CT_MACHINE_IMG,
    machineAngles: [
      { url: CT_ANGLES[0], caption: "CT Gantry — Front View" },
      { url: CT_ANGLES[1], caption: "CT Scanner — Patient Table" },
      { url: CT_ANGLES[2], caption: "CE 32-Slice CT Scanner" },
    ],
    tagline:
      "Fast, precise cross-sectional imaging for emergency and routine diagnosis.",
    about:
      "CT (Computed Tomography) uses X-ray beams from multiple angles combined with computer processing to produce detailed cross-sectional images of bones, blood vessels and soft tissues. Our 32-Slice CE-certified GE Brivo 32 scanner acquires images in seconds — critical for trauma and emergency care.",
    machine: {
      name: "GE Brivo 32",
      specs: [
        { label: "Slices", value: "32 Slices / rotation" },
        { label: "Rotation Time", value: "0.5 seconds" },
        { label: "Coverage", value: "160 mm / rotation" },
        { label: "Resolution", value: "Sub-millimetre" },
        { label: "Radiation", value: "Ultra-low dose AI" },
        { label: "3D Recon.", value: "Real-time rendering" },
      ],
    },
    whatWeDetect: [
      "Brain stroke, haemorrhage & tumors",
      "Pulmonary embolism & lung nodules",
      "Abdominal organ injuries & masses",
      "Bone fractures & spinal injuries",
      "Coronary artery disease (CTA)",
      "Aneurysms & vascular disease",
      "Kidney stones & urinary tract",
      "Cancer staging & follow-up",
    ],
    bodyParts: [
      { emoji: "🧠", name: "Brain & Head" },
      { emoji: "🦷", name: "Sinuses & Face" },
      { emoji: "🫁", name: "Chest & Lungs" },
      { emoji: "🫀", name: "Cardiac CTA" },
      { emoji: "🩺", name: "Abdomen" },
      { emoji: "🦴", name: "Spine & Bones" },
      { emoji: "🦵", name: "Extremities" },
      { emoji: "🩸", name: "Angiography" },
    ],
    prep: [
      "Fast for 4 hours before abdominal CT",
      "Remove metal jewellery and accessories",
      "Inform us of any contrast dye allergy",
      "Inform of pregnancy or kidney disease",
      "Wear loose, comfortable clothing",
    ],
    duration: "10 – 20 min",
    report: "2 – 4 hrs",
    contrast: "Sometimes required",
    radiation: "Low dose",
  },
  mri: {
    id: "mri",
    label: "MRI Scan",
    sublabel: "Magnetic Resonance Imaging",
    color: "#7c3aed",
    lightBg: "bg-violet-50",
    textColor: "text-violet-600",
    borderColor: "border-violet-200",
    machineImg: MRI_MACHINE_IMG,
    machineAngles: [
      { url: MRI_ANGLES[0], caption: "1.5T MRI Bore — Front View" },
      { url: MRI_ANGLES[1], caption: "MRI Scanner — Patient Entry" },
      { url: MRI_ANGLES[2], caption: "MRI Suite — Indur Centre" },
    ],
    tagline:
      "Superior soft tissue imaging with zero radiation — gold standard for neuro, oncology & MSK.",
    about:
      "MRI uses powerful magnetic fields and radio waves — no ionising radiation — to generate exquisitely detailed images of soft tissues, organs, and the nervous system. Our 1.5 Tesla Hdxt delivers clinical-grade imaging across all body systems with rapid acquisition protocols.",
    machine: {
      name: "1.5 Tesla Hdxt",
      specs: [
        { label: "Field Strength", value: "1.5 Tesla Hdxt" },
        { label: "Magnet Type", value: "Superconducting" },
        { label: "Channels", value: "16 Channel" },
        { label: "Coils Available", value: "Head, Spine, Body, MSK" },
        { label: "Radiation", value: "Zero — RF waves only" },
        { label: "Gradient Strength", value: "33 mT/m" },
      ],
    },
    whatWeDetect: [
      "Brain tumors, MS, stroke & epilepsy",
      "Spinal disc herniation & cord lesions",
      "Knee, shoulder, hip — ligament/cartilage",
      "Liver, pancreas & biliary (MRCP)",
      "Uterus, ovaries & prostate pathology",
      "MR Angiography — vessels non-invasively",
      "Breast MRI for cancer staging",
      "Whole-body oncology screening",
    ],
    bodyParts: [
      { emoji: "🧠", name: "Brain & Neuro" },
      { emoji: "🦷", name: "Head & Neck" },
      { emoji: "🦴", name: "Spine & Cord" },
      { emoji: "🫀", name: "Cardiac MRI" },
      { emoji: "🩺", name: "Abdomen & Pelvis" },
      { emoji: "🦵", name: "Joints & MSK" },
      { emoji: "🩸", name: "MR Angiography" },
      { emoji: "🔬", name: "MRCP / MR Urog." },
    ],
    prep: [
      "Remove all metallic jewellery, accessories, etc. before entering the scan room",
      "⚠️ Pacemakers are strictly prohibited inside the MRI room — please inform staff immediately if you have one",
      "If you have any implants (rods, plates, screws, clips), please inform the staff before the scan",
      "No food 4–6 hours for abdominal MRI",
      "Change into center gown provided",
      "Claustrophobic patients: inform in advance",
      "Kidney patients: alert us before contrast",
    ],
    duration: "10 – 15 min",
    report: "2 – 4 hrs",
    contrast: "Gadolinium (sometimes)",
    radiation: "ZERO — no X-rays",
  },
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */
function QuickStatPill({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ background: `${color}15` }}
      >
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <div className="text-[11px] text-gray-400 uppercase tracking-wider">
          {label}
        </div>
        <div className="text-sm font-bold text-gray-800">{value}</div>
      </div>
    </div>
  );
}

/* ─── Main Section ─────────────────────────────────────────────────────── */
export default function ScansDetailSection() {
  const [active, setActive] = useState("ct");
  const scan = scans[active];

  return (
    <section id="scan-detail" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ══ Heading ══ */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="section-subtitle">In-Depth Guide</p>
          <h2 className="section-title text-gray-900 mb-4">
            Everything You Need to <span className="gradient-text">Know</span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            What each scan detects, preparation instructions, body parts
            covered, and when your doctor recommends it.
          </p>
        </motion.div>

        {/* ══ Tab switcher ══ */}
        <motion.div
          variants={scaleIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex justify-center mb-12"
        >
          <div className="inline-flex bg-white border border-gray-200 rounded-2xl p-1.5 shadow-sm gap-1">
            {Object.values(scans).map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                className={`relative px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  active === s.id
                    ? "text-white shadow-lg"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {active === s.id && (
                  <motion.span
                    layoutId="tab-pill"
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background: `linear-gradient(135deg, ${s.color}, ${s.color}bb)`,
                    }}
                  />
                )}
                <span className="relative z-10">{s.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* ══ Tab content ══ */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Row 1: Machine close-up + overview */}
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              {/* Machine photo (detailed view) */}
              <motion.div
                variants={fadeLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="relative rounded-3xl overflow-hidden shadow-xl bg-black h-72 lg:h-auto group"
              >
                <img
                  src={scan.machineImg}
                  alt={`${scan.machine.name} at Indur CT & MRI Centre`}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div
                    className="inline-block text-xs font-bold px-3 py-1 rounded-full text-white mb-2"
                    style={{ background: scan.color }}
                  >
                    {scan.label}
                  </div>
                  <div className="text-white font-poppins font-bold text-xl">
                    {scan.machine.name}
                  </div>
                  <div className="text-white/60 text-sm">{scan.sublabel}</div>
                </div>
              </motion.div>

              {/* Overview */}
              <motion.div
                variants={fadeRight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 flex flex-col justify-between"
              >
                <div>
                  <div
                    className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${scan.lightBg} ${scan.textColor} mb-4`}
                  >
                    {scan.sublabel}
                  </div>
                  <p
                    className="text-gray-700 font-medium text-lg leading-relaxed mb-4 italic border-l-4 pl-4"
                    style={{ borderColor: scan.color }}
                  >
                    "{scan.tagline}"
                  </p>
                  <p className="text-gray-500 leading-relaxed mb-6">
                    {scan.about}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <QuickStatPill
                    icon={Clock}
                    label="Duration"
                    value={scan.duration}
                    color={scan.color}
                  />
                  <QuickStatPill
                    icon={Eye}
                    label="Report"
                    value={scan.report}
                    color={scan.color}
                  />
                  <QuickStatPill
                    icon={Activity}
                    label="Contrast"
                    value={scan.contrast}
                    color={scan.color}
                  />
                  <QuickStatPill
                    icon={Zap}
                    label="Radiation"
                    value={scan.radiation}
                    color={scan.color}
                  />
                </div>
              </motion.div>
            </div>

            {/* Leadership */}
            <div className="grid sm:grid-cols-2 gap-5 mb-8">
              {[
                {
                  name: "Dr. E. Ravindra Reddy",
                  role: "Chairman of Indur CT & MRI Centre Nizamabad",
                  sub: "Dr. E. Ravindra Reddy is a Senior Pediatrician with over 44 years of experience in pediatric and neonatal intensive care. He served as Chairman of the AP Medical Council from 2010 to 2016 and as Chairman of the Telangana State Medical Council from 2016 to 2021. He has also held the position of National Vice President of the Indian Medical Association and has extensive experience at SVR Hospital, Nizamabad.",
                },
                {
                  name: "Dr. D.L.N. Swamy",
                  role: "Managing Director (MD) of Indur CT & MRI Centre Nizamabad",
                  sub: "Dr. D.L.N. Swamy, MBBS, MD, is a Senior Physician with over 44 years of clinical experience. He previously served as the Superintendent of the District Headquarters Hospital and currently serves as Chairman of Harika Hospital, Nizamabad.",
                },
              ].map(({ name, role, sub }) => (
                <motion.div
                  key={name}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex flex-col gap-4 rounded-2xl p-6 border bg-white shadow-sm"
                  style={{ borderColor: `${scan.color}30` }}
                >
                  {/* Avatar + name row */}
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-poppins font-black text-xl shrink-0 shadow-md"
                      style={{
                        background: `linear-gradient(135deg, ${scan.color}, ${scan.color}aa)`,
                      }}
                    >
                      {name.charAt(4)}
                    </div>
                    <div>
                      <div className="font-poppins font-bold text-gray-900 text-sm leading-tight">
                        {name}
                      </div>
                      <span
                        className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full mt-1 text-white"
                        style={{ background: scan.color }}
                      >
                        {role}
                      </span>
                    </div>
                  </div>
                  {/* Divider */}
                  <div
                    className="h-px w-full"
                    style={{ background: `${scan.color}20` }}
                  />
                  {/* Bio */}
                  <p className="text-xs text-gray-500 leading-relaxed">{sub}</p>
                </motion.div>
              ))}
            </div>

            {/* Row 3: Detect */}
            <div className="mb-8">
              {/* What we detect */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-7 border border-gray-100 shadow-sm"
              >
                <h3 className="font-poppins font-bold text-lg text-gray-900 mb-5">
                  What This Scan Detects
                </h3>
                <ul className="space-y-3">
                  {scan.whatWeDetect.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-gray-600"
                    >
                      <CheckCircle
                        className="w-4 h-4 shrink-0 mt-0.5"
                        style={{ color: scan.color }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Row 4: Preparation */}
            <motion.div
              variants={scaleIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="rounded-3xl p-8 sm:p-10"
              style={{
                background: `${scan.color}10`,
                border: `1px solid ${scan.color}25`,
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle
                      className="w-5 h-5"
                      style={{ color: scan.color }}
                    />
                    <h3 className="font-poppins font-bold text-xl text-gray-900">
                      How to Prepare for Your {scan.label}
                    </h3>
                  </div>
                  <ul className="space-y-2.5">
                    {scan.prep.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-3 text-sm text-gray-600"
                      >
                        <ChevronRight
                          className="w-4 h-4 shrink-0 mt-0.5"
                          style={{ color: scan.color }}
                        />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="shrink-0 bg-white rounded-2xl p-8 shadow-md text-center border border-gray-100 w-full sm:w-96">
                  <div className="text-3xl mb-2">📋</div>
                  <div className="font-poppins font-bold text-gray-900 mb-1">
                    Ready to Book?
                  </div>
                  <div className="text-gray-500 text-sm mb-4">
                    Walk in or call — no long waits.
                  </div>
                  <a
                    href="tel:+916301195478"
                    className="block w-full text-center text-white font-bold py-3 rounded-xl shadow-lg hover:opacity-90 transition-all"
                    style={{
                      background: `linear-gradient(135deg, ${scan.color}, ${scan.color}cc)`,
                    }}
                  >
                    📞 +91 63011 95478
                  </a>
                  <a
                    href="#appointment"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("appointment")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="block w-full text-center font-semibold py-2.5 rounded-xl mt-2 border transition-all"
                    style={{
                      color: scan.color,
                      borderColor: `${scan.color}40`,
                    }}
                  >
                    Book Online
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* ══ OUR CENTRE Gallery ══ */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-20"
        >
          <motion.div variants={fadeUp} className="text-center mb-10">
            <p className="section-subtitle">Photo Gallery</p>
            <h2 className="section-title text-gray-900 mb-3">
              Our <span className="gradient-text">Centre</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              A look inside Indur CT &amp; MRI Centre — state-of-the-art
              facilities built for your comfort and care.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { src: indurBoard, caption: "Indur CT & MRI Centre — Board" },
              { src: indurBoard2, caption: "Indur Centre — Signboard" },
              { src: indurEntrance, caption: "Indur Centre — Entrance" },
            ].map(({ src, caption }, i) => (
              <motion.div
                key={caption}
                variants={fadeUp}
                whileHover={{ scale: 1.03, y: -4 }}
                className="relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer"
              >
                <img
                  src={src}
                  alt={caption}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div
                  className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold text-white"
                  style={{ background: "#e8523a" }}
                >
                  Indur Centre
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-white text-sm font-semibold">
                    {caption}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
